import Styles from './LoginPage.module.css'
import styled from 'styled-components'
import logo from 'Assets/Images/logo.png'
import { TextField, FormGroup } from '@mui/material'
import IFText from 'Components/IFText/IFText'
import IFButton from 'Components/IFButton/IFButton'
import { Form, Formik, Field, ErrorMessage } from 'formik'
import { Colors } from 'Theme'
import { object, string } from 'yup'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import AuthActions from 'Stores/Auth/Actions'
import AuthSelectors from 'Stores/Auth/Selectors'
import { styled as muiStyled } from '@mui/system'
import { useNavigate } from 'react-router-dom'
import ReactPinField from 'react-pin-field'
import { useState, useEffect } from 'react'
import { QRCodeSVG } from 'qrcode.react'

const initialValues = {
  username: '',
  password: '',
}

const FormContainer = styled.div`
  background-color: ${Colors.white};
`

const ErrorContainer = styled.div`
  color: ${Colors.red};
`
const CssTextField = muiStyled(TextField)(({ theme }) => ({
  '& label.Mui-focused': {
    color: Colors.text,
  },
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused fieldset': {
      borderColor: Colors.primary,
    },
  },
  '& .MuiOutlinedInput-root': {
    '&:hover fieldset': {
      borderColor: Colors.primary,
    },
    '&.Mui-focused fieldset': {
      borderColor: Colors.primary,
    },
  },
}))

const StyledPinField = muiStyled(ReactPinField)(() => ({
  backgroundColor: `${Colors.pinFieldBackground}`,
  border: `1px solid ${Colors.pinFieldBorder}`,
  borderRadius: '0.3rem',
  fontSize: '2rem',
  margin: '0.25rem',
  height: '3.5rem',
  outline: 'none',
  textAlign: 'center',
  width: '3rem',
  '&:focus': {
    borderColor: `${Colors.primary}`,
    transform: 'scale(1.05)',
  },
}))

const LoginPage = ({
  isLoginLoading,
  login,
  challengeId,
  requires2FASetup,
  otpauthUrl,
  manualKey,
  requires2FA,
  onTwoFactorVerify,
  loginToken,
  onTwoFactorSetupVerify,
  setUp2FaComplete,
  setLoginToken,
  resetAuthState,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [confirmPin, setConfirmPin] = useState('')
  const [step, setStep] = useState('instructions')

  useEffect(() => {
    resetAuthState()

    const handlePopState = () => resetAuthState()
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  useEffect(() => {
    if (setUp2FaComplete) navigate('/login')
  }, [setUp2FaComplete])

  return (
    <div
      className={Styles.BackgroundDiv}
      style={{ backgroundColor: Colors.LoginBackground }}
    >
      <div
        className={Styles.Container}
        style={{
          backgroundColor: Colors.white,
          boxShadow: `0 0 20px 3px ${Colors.LoginBackgroundShadow}`,
        }}
      >
        <div
          className={Styles.WelcomeCard}
          style={{
            background: `linear-gradient(to bottom left,${Colors.LoginGradient1} ,  ${Colors.LoginGradient2} )`,
          }}
        >
          <IFText
            className={Styles.WelcomeTitle}
            style={{ color: Colors.white }}
          >
            {t('LoginPage.WelcomeTitle')}
          </IFText>

          <IFText
            className={Styles.WelcomeText}
            style={{ color: Colors.LoginBackground }}
          >
            {t('LoginPage.WelcomeText')}
          </IFText>
        </div>

        <div className={Styles.FormDiv}>
          <div className={Styles.LogoWrappingDiv}>
            <img className={Styles.Logo} src={logo} alt="logo" />
          </div>

          {requires2FASetup ? (
            step === 'instructions' ? (
              <div className={Styles.StepContainer}>
                <IFText className={Styles.TwoFaSetupTitle}>
                  {t('LoginPage.Setup2faTitle')}
                </IFText>

                <IFText
                  style={{
                    color: Colors.gray,
                  }}
                  className={Styles.TwoFaSetupSubtitle}
                >
                  {t('LoginPage.Setup2faSubtitle')}
                </IFText>

                <div className={Styles.StepRow}>
                  <div
                    className={Styles.StepNumber}
                    style={{ background: Colors.primary, color: Colors.white }}
                  >
                    1
                  </div>

                  <div>
                    <IFText className={Styles.TwoFaTitles}>
                      {t('LoginPage.DownloadAppTitle')}
                    </IFText>

                    <IFText style={{ fontSize: 14, color: Colors.gray }}>
                      {t('LoginPage.DownloadAppSubtitle')}
                    </IFText>
                  </div>
                </div>

                <div className={Styles.StepRow}>
                  <div
                    className={Styles.StepNumber}
                    style={{ background: Colors.primary, color: Colors.white }}
                  >
                    2
                  </div>
                  <div>
                    <IFText className={Styles.TwoFaTitles}>
                      {t('LoginPage.ScanTitle')}
                    </IFText>
                    <IFText style={{ color: Colors.gray }}>
                      {t('LoginPage.ScanSubtitle')}
                    </IFText>
                  </div>
                </div>

                {otpauthUrl && (
                  <div className={Styles.QrCode}>
                    <QRCodeSVG value={otpauthUrl} size={100} />
                  </div>
                )}

                {manualKey && (
                  <div className={Styles.ManualKeyWrapper}>
                    <IFText className={Styles.ManualKeyTitle}>
                      {t('LoginPage.ManualKey')}
                    </IFText>
                    <div
                      className={Styles.ManualKeyDiv}
                      style={{ background: Colors.CardShadow }}
                    >
                      {manualKey}
                    </div>
                  </div>
                )}

                <IFButton
                  className={Styles.ContinueButton}
                  color={Colors.primary}
                  isFill
                  text="Continue"
                  onClick={() => setStep('pin')}
                />
              </div>
            ) : (
              <div className={Styles.PinConatiner}>
                <IFText className={Styles.PinTitle}>
                  {t('LoginPage.Pin')}
                </IFText>

                <div className={Styles.Pins}>
                  <StyledPinField
                    length={6}
                    validate="0123456789"
                    onChange={(value) => {
                      setConfirmPin(value)
                      setLoginToken(value)
                    }}
                  />
                </div>

                <IFButton
                  color={Colors.primary}
                  isFill
                  isDead={confirmPin.length !== 6}
                  text="Verify"
                  onClick={() => {
                    onTwoFactorSetupVerify(challengeId, loginToken)
                  }}
                />
              </div>
            )
          ) : requires2FA ? (
            <div className={Styles.PinConatiner}>
              <IFText className={Styles.PinTitle}>{t('LoginPage.Pin')}</IFText>

              <div className={Styles.Pins}>
                <StyledPinField
                  length={6}
                  validate="0123456789"
                  onChange={(value) => {
                    setConfirmPin(value)
                    setLoginToken(value)
                  }}
                />
              </div>
              <IFButton
                color={Colors.primary}
                isFill
                isDead={confirmPin.length !== 6}
                text="Verify"
                onClick={() => {
                  onTwoFactorVerify(challengeId, loginToken)
                }}
              />
            </div>
          ) : (
            <FormContainer className={Styles.FormWrappingDiv}>
              <Formik
                validationSchema={object({
                  username: string().required(t('LoginPage.Required')),
                  password: string()
                    .required(t('LoginPage.Required'))
                    .min(8, t('LoginPage.ShortPassword')),
                })}
                initialValues={initialValues}
                onSubmit={(values) => {
                  login(values.username, values.password)
                }}
              >
                {({ values, errors }) => (
                  <Form>
                    <FormGroup className={Styles.InputWrappingDiv}>
                      <Field
                        sx={{
                          '& .MuiInputBase-input': {
                            fontFamily: 'ProximaNova',
                            fontSize: '14px',
                          },
                        }}
                        className={Styles.Input}
                        size="small"
                        name="username"
                        as={CssTextField}
                        variant="outlined"
                        label="Email"
                        InputLabelProps={{
                          shrink: true,
                          style: {
                            color: Colors.primary,
                            fontFamily: 'ProximaNova',
                          },
                        }}
                      />
                      <ErrorContainer className={Styles.ErrorStyling}>
                        <ErrorMessage name="username" />
                      </ErrorContainer>
                    </FormGroup>

                    <div
                      className={Styles.ForgotPasswordDiv}
                      onClick={() => {
                        navigate('/reset')
                      }}
                    >
                      <IFText
                        className={Styles.ForgotPassword}
                        style={{ color: Colors.ForgotPassword }}
                      >
                        {t('LoginPage.ForgotPassword')}
                      </IFText>
                    </div>
                    <FormGroup>
                      <Field
                        sx={{
                          '& .MuiInputBase-input': {
                            fontFamily: 'ProximaNova',
                            fontSize: '14px',
                          },
                        }}
                        as={CssTextField}
                        className={Styles.Input}
                        name="password"
                        type="password"
                        size="small"
                        variant="outlined"
                        label="Password"
                        InputLabelProps={{
                          shrink: true,
                          style: {
                            color: Colors.primary,
                            fontFamily: 'ProximaNova',
                          },
                        }}
                      />
                      <ErrorContainer className={Styles.ErrorStyling}>
                        <ErrorMessage name="password" />
                      </ErrorContainer>
                    </FormGroup>

                    <IFButton
                      className={Styles.ButtonWrappingDiv}
                      color={Colors.primary}
                      isDead={
                        !(values.username && values.password) ||
                        (errors.password && errors.password.length > 0) ||
                        (errors.username && errors.username.length > 0)
                      }
                      isFill={true}
                      isLoading={isLoginLoading}
                      text={t('LoginPage.Login')}
                    />
                  </Form>
                )}
              </Formik>
            </FormContainer>
          )}
        </div>
      </div>
    </div>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    login: (email, password) => dispatch(AuthActions.login(email, password)),
    onTwoFactorVerify: (challengeId, token) =>
      dispatch(AuthActions.onTwoFactorVerify(challengeId, token)),
    onTwoFactorSetupVerify: (challengeId, token) =>
      dispatch(AuthActions.onTwoFactorSetupVerify(challengeId, token)),
    setLoginToken: (loginToken) =>
      dispatch(AuthActions.setLoginToken(loginToken)),
    resetAuthState: () => dispatch(AuthActions.resetAuthState()),
  }
}

const mapStateToProps = (state) => ({
  isLoginLoading: AuthSelectors.isLoginLoading(state),
  requires2FASetup: AuthSelectors.getRequires2FASetup(state),
  requires2FA: AuthSelectors.getRequires2FA(state),
  challengeId: AuthSelectors.getChallengeId(state),
  otpauthUrl: AuthSelectors.getOtpauthUrl(state),
  manualKey: AuthSelectors.getManualKey(state),
  loginToken: AuthSelectors.getLoginToken(state),
  setUp2FaComplete: AuthSelectors.getSetUp2FaComplete(state),
})

export default connect(mapStateToProps, mapDispatchToProps)(LoginPage)
